import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { dealApi, dealQueryKeys } from '@crm/entities/deal';
import { dealsBoardQueryKeys } from '@crm/entities/deals-board';
import { toastManager } from '@crm/entities/toast';
import { getApiErrorMessage } from '@crm/shared/api';

import type { LostDealFormValues, TLostDealInvalidationScope } from '../model/types';
import type { TDeal } from '@crm/entities/deal';

export const useLostDealMutation = ({
    dealIds,
    invalidationScope,
}: {
    dealIds: readonly TDeal['id'][];
    invalidationScope: TLostDealInvalidationScope;
}) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const invalidateRelatedQueries = async () => {
        const sourceQueryKeys: Record<
            TLostDealInvalidationScope,
            readonly unknown[] | null
        > = {
            board: null,
            postponed: dealQueryKeys.postponedDeals.all(),
            inWorkOrFinished: dealQueryKeys.inWorkOrFinishedDeals.all(),
        };
        const sourceQueryKey = sourceQueryKeys[invalidationScope];

        const queryKeys = [
            ...dealIds.map(dealQueryKeys.detail),
            dealQueryKeys.lostDeals.all(),
            ...(sourceQueryKey ? [sourceQueryKey] : []),
        ];

        await Promise.all([
            ...queryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
            queryClient.invalidateQueries({
                queryKey: dealsBoardQueryKeys.board(),
                exact: true,
                refetchType: 'all',
            }),
        ]);
    };

    const { mutateAsync: lostDealMutationFn, isPending } = useMutation({
        mutationKey: ['deal', 'lost'],
        mutationFn: (data: LostDealFormValues) => {
            if (dealIds.length === 0 || new Set(dealIds).size !== dealIds.length) {
                throw new Error(t('crm.common.deal.errors.invalidIds'));
            }

            return dealApi.lostDeals({
                dealIds,
                payload: {
                    reason: data.reason,
                },
            });
        },
        onSuccess: async () => {
            await invalidateRelatedQueries();

            toastManager.onSuccess({
                title:
                    dealIds.length > 1
                        ? t('crm.lostDeal.notifications.success.batch', {
                              count: dealIds.length,
                          })
                        : t('crm.lostDeal.notifications.success.single'),
            });
        },
        onError: (error) => {
            toastManager.onError({
                title: getApiErrorMessage(error, t('crm.lostDeal.errors.submit')),
            });
        },
    });

    return {
        lostDealMutationFn,
        isPending,
    };
};
